/**
 * Circular-safe JSON stringify
 */
export function exportToCsv(filename: string, rows: any[], columns: { key: string, label: string }[]) {
  if (!rows || !rows.length) return;

  const header = columns.map(c => `"${c.label.replace(/"/g, '""')}"`).join(',');
  const csvContent = [
    header,
    ...rows.map(row => columns.map(c => {
      let val = row[c.key];
      if (val === null || val === undefined) val = '';
      if (typeof val === 'object') {
         if (val?.toDate) val = val.toDate().toISOString();
         else val = safeStringify(val);
      }
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function safeStringify(obj: any, replacer?: any, indent?: number | string): string {
  const cache = new Set();
  try {
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.has(value)) {
          return '[Circular]';
        }
        cache.add(value);

        // Prevent DOM nodes from being stringified
        if ('nodeType' in value && typeof value.nodeType === 'number') {
          return `[DOM Node: ${value.nodeName}]`;
        }
        
        // Handle Fiber nodes or React internal structures that cause deep recursion/circularity
        if (value.constructor && (value.constructor.name === 'FiberNode' || value.constructor.name === 'SyntheticEvent')) {
            return `[React Internal: ${value.constructor.name}]`;
        }
      }

      // Handle common non-serializable objects that cause circularity or info loss
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
          code: (value as any).code
        };
      }

      if (replacer && typeof replacer === 'function') {
        return replacer(key, value);
      }
      return value;
    }, indent);
  } catch (err) {
    console.warn(`[safeStringify] Serialization failed:`, err);
    return '"[Unserializable]"';
  }
}

/**
 * Helper to strip markdown and format text for smooth, human-like speech.
 * Ensures section labels have pauses and eliminates formatting artifacts.
 */
export function cleanTextForSpeech(text: string): string {
  if (!text) return '';
  
  let cleaned = text;

  // Remove code fences completely or keep content without backticks
  cleaned = cleaned.replace(/```(?:[a-zA-Z]*)\n([\s\S]*?)```/g, ' $1 ');

  // Remove inline code markers
  cleaned = cleaned.replace(/`([^`]+)`/g, ' $1 ');

  // Handle Tables (strip table formatting characters: |, -, :, etc)
  cleaned = cleaned.replace(/^\|.*\|$/gm, (match) => {
    return match.replace(/\|/g, ' ').replace(/-/g, ' ');
  });

  // Remove HTML tags
  cleaned = cleaned.replace(/<[^>]+>/g, ' ');

  // Decode basic HTML entities
  cleaned = cleaned.replace(/&amp;/g, ' and ');
  cleaned = cleaned.replace(/&lt;/g, ' less than ');
  cleaned = cleaned.replace(/&gt;/g, ' greater than ');
  cleaned = cleaned.replace(/&nbsp;/g, ' ');

  // Remove image syntax ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, ' ');

  // Remove link syntax [text](url) -> keeps only the text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Simplfy or remove raw URLs
  cleaned = cleaned.replace(/https?:\/\/[^\s]+/g, ' link ');

  // Handle dollar signs
  cleaned = cleaned.replace(/\$(\d+(?:\.\d+)?)/g, '$1 dollars ');

  // Remove escaped markdown artifacts (e.g. \*, \_, \#)
  cleaned = cleaned.replace(/\\[*_#~n]/g, ' ');
  cleaned = cleaned.replace(/\\n/g, ' ');

  // Remove blockquote markers
  cleaned = cleaned.replace(/^[ \t]*>\s+/gm, ' ');

  // Process headers into clear section labels with terminal punctuation for pauses
  cleaned = cleaned.replace(/^[ \t]*#{1,6}\s*(?:\d+\.)?\s*([^:\n]+)(?:[:\.]?)/gm, (_, label) => {
    return `${label.trim()}. `;
  });

  // Remove bold/italic paired markers
  cleaned = cleaned.replace(/(\*\*|__)(.*?)\1/g, ' $2 ');
  cleaned = cleaned.replace(/(\*|_)(.*?)\1/g, ' $2 ');

  // Remove list markers (bullets)
  cleaned = cleaned.replace(/^[ \t]*[-*+]\s+/gm, '');
  cleaned = cleaned.replace(/^[ \t]*•\s+/gm, '');

  // Keep numbered list prefixes format, but ensure it reads naturally (usually \d+\. is fine in text-to-speech)

  // Remove remaining markdown markers and visual symbols (but keep punctuation like ,.?!;)
  cleaned = cleaned.replace(/[*_~#|<-]/g, ' ');
  // Avoid replacing $ and > if they are part of natural language, but above we cleaned HTML and blockquotes.
  // Be careful not to remove safe characters.

  // Normalize whitespace and handle line breaks for natural speech rhythm
  cleaned = cleaned.split('\n')
    .map(line => {
      let l = line.trim();
      if (!l) return '';
      // If it ends without punctuation, maybe add a period for natural pause
      if (!/[.!?;:]$/.test(l)) {
        l += '.';
      }
      return l;
    })
    .filter(Boolean)
    .join(' ');

  // Final cleanup of multiple spaces and ensure sentences have pauses
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  // Ensure the whole thing is trimmed
  return cleaned.trim();
}

/**
 * Safe conversion to array for any value.
 * Useful for mapping over potentially malformed/object-based data from Firestore.
 */
export function toArray<T>(value: any): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "object") return Object.values(value) as T[];
  return [];
}

/**
 * Detects if the current environment is a mobile device or resource-constrained.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isNarrowScreen = window.innerWidth < 1024;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return isMobileUA || (isNarrowScreen && isTouchDevice);
}

export function getFriendlyErrorMessage(err: any): string {
    const errStr = typeof err === 'string' ? err : String(err?.message || err);
    const errLower = errStr.toLowerCase();

    // Map raw technical errors
    if (errLower.includes('pdf is not a function')) {
      return "There was an issue reading the PDF format. Please try saving it as a standard PDF and upload again.";
    }
    if (errLower.includes('map is not a function') || errLower.includes('undefined is not iterable')) {
      return "The AI returned an unexpected format and we could not process the lessons. Please try regenerating.";
    }
    if (errLower.includes('permission denied') || errLower.includes('missing or insufficient permissions')) {
      return "You don't have permission to perform this action. Please check your login status or contact an administrator.";
    }
    if (errLower.includes('course empty after retry') || errStr.includes('COURSE_EMPTY_AFTER_RETRY')) {
      return "The AI could not extract a meaningful course structure from the provided sources. Please try different or more detailed documents.";
    }
    if (errLower.includes('failed to fetch') || errLower.includes('networkerror')) {
      return "Could not connect to the server. Please check your internet connection.";
    }
    if (errLower.includes('client_side_ai_call_blocked')) {
      return "Invalid request: AI operations must run on the server.";
    }
    if (errLower.includes('source_content_mismatch') || errStr.includes('SOURCE_CONTENT_MISMATCH')) {
      return "The uploaded file was received, but the generated content does not match the current source. Please try again.";
    }

    const mapping: Record<string, string> = {
      'TEXT_EXTRACTION_EMPTY': 'The document was uploaded, but Lumina could not extract enough readable text.',
      'SCANNED_OR_UNREADABLE': 'This PDF may be scanned or image-based. Please upload a clearer file or try desktop processing.',
      'TEXT_EXTRACTION_WEAK': 'Lumina could only extract a small amount of text from this document. The course may be limited.',
      'CHUNKING_EMPTY': 'Could not identify meaningful learning sections from the content provided.',
      'PLANNER_RETURNED_EMPTY': 'The source was readable, but the planner could not build a valid course structure.',
      'ALL_SOURCES_FAILED_EXTRACTION': 'Source processing failed. Ensure your documents are readable or the provided website URLs allow text extraction.',
      'PLANNER_SCHEMA_INVALID': 'The AI generated a structure that did not match the expected format. Retrying...',
      'QUOTA_EXCEEDED': 'Lumina is currently at capacity. Please try again in a few minutes.',
      'OCR_FAILED': 'Optical character recognition failed to read the document. Please try a cleaner or digital PDF.'
    };
    
    // Check direct mapped keys first
    for (const key of Object.keys(mapping)) {
      if (errStr.includes(key)) {
        return mapping[key];
      }
    }

    return errStr;
}
